// Shareable deal links: /d/{source}/{key}.
// The Pages Function in frontend/functions imports this module too, so it must stay
// free of browser globals and JSX at import time.
/* global globalThis */

export const DEAL_SOURCES = Object.freeze(['wine', 'marketplace']);
export const DEAL_KEY_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
export const DEAL_PARAM = 'deal';

export const TRACKER_PATHS = Object.freeze({
  wine: '/tools/qfwine',
  marketplace: '/tools/qfmarketplace',
});

export const isDealSource = (value) => DEAL_SOURCES.includes(value);
export const isDealKey = (value) => typeof value === 'string' && DEAL_KEY_PATTERN.test(value);

// The deal key is the last non-empty path segment of the product's Qantas URL,
// e.g. https://wine.qantas.com/p/pertaringa-…-2022/PERTIPCAB22 -> PERTIPCAB22.
// Anything that isn't a short, URL-safe code gives null, and the row then has no
// share link or price history.
export const dealKeyFromUrl = (url) => {
  if (typeof url !== 'string' || !url.trim()) return null;
  let path = url.trim();
  try {
    path = new URL(path).pathname;
  } catch {
    path = path.split(/[?#]/)[0];
  }
  const segments = path.split('/').filter(Boolean);
  const key = segments[segments.length - 1];
  return isDealKey(key) ? key : null;
};

export const dealSharePath = (source, key) => `/d/${source}/${key}`;

export const dealShareUrl = (origin, source, key) => `${String(origin || '').replace(/\/+$/, '')}${dealSharePath(source, key)}`;

// Where /d/{source}/{key} lands in the app. Unknown sources fall back to the Wine
// Tracker; the tracker explains a key it can't find.
export const dealLandingPath = (source, key) => {
  const tracker = TRACKER_PATHS[source] || TRACKER_PATHS.wine;
  const value = typeof key === 'string' ? key.trim() : '';
  return value ? `${tracker}?${DEAL_PARAM}=${encodeURIComponent(value)}` : tracker;
};

// Web Share where the device has it, otherwise the clipboard, otherwise the caller
// shows the link to copy by hand. Resolves to 'shared', 'cancelled', 'copied' or
// 'manual'.
export const shareDeal = async ({ title, text, url }, nav = globalThis.navigator) => {
  if (nav && typeof nav.share === 'function') {
    try {
      await nav.share({ title, text, url });
      return 'shared';
    } catch (error) {
      if (error?.name === 'AbortError') return 'cancelled';
      // Some browsers refuse share() outside a user gesture or for some data;
      // fall through to the clipboard.
    }
  }
  try {
    if (!nav?.clipboard?.writeText) return 'manual';
    await nav.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'manual';
  }
};
